import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { jwtDecode } from 'jwt-decode';
import './VerifyLogin.css';

const VerifyLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [status, setStatus] = useState('confirming'); // confirming, verifying, success, error
  const [message, setMessage] = useState('Please confirm your login');
  const [tokenInfo, setTokenInfo] = useState(null);

  useEffect(() => {
    const checkTokenStatus = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid login link. Please request a new one.');
        return;
      }

      // First, check token status without consuming it
      try {
        const statusResponse = await fetch('/api/auth/check-token-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
          },
          body: JSON.stringify({ token }),
        });

        const statusData = await statusResponse.json();
        
        if (statusData.status === 'valid') {
          setTokenInfo({ token, expiresInMinutes: statusData.expiresInMinutes });
          setMessage('Click the button below to complete your login');
        } else if (statusData.status === 'used') {
          setStatus('error');
          setMessage('This login link has already been used. Please request a new one.');
        } else if (statusData.status === 'expired') {
          setStatus('error');
          setMessage('This login link has expired. Please request a new one.');
        } else {
          setStatus('error');
          setMessage('This login link is invalid. Please request a new one.');
        }
      } catch (error) {
        console.error('Token status check error:', error);
        setStatus('error');
        setMessage('Unable to verify login link. Please try again.');
      }
    };

    checkTokenStatus();
  }, [searchParams]);

  const handleConfirmLogin = async () => {
    if (!tokenInfo || !tokenInfo.token) return;
    
    setStatus('verifying');
    setMessage('Logging you in...');
    
    // Clear any existing auth data to prevent conflicts
    sessionStorage.clear();
    
    try {
      const response = await fetch('/api/auth/verify-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ token: tokenInfo.token }),
      });

        const data = await response.json();

        if (response.ok) {
          // Store auth data
          sessionStorage.setItem('token', data.token);
          sessionStorage.setItem('username', data.username);
          sessionStorage.setItem('email', data.email);
          sessionStorage.setItem('id', data.id);
          sessionStorage.setItem('fullName', data.fullName || '');
          sessionStorage.setItem('role', data.role || 'User');

          // Decode the JWT and update auth context
          try {
            const decodedUser = jwtDecode(data.token);
            setAuth({ 
              token: data.token, 
              isAuthenticated: true, 
              user: decodedUser 
            });
          } catch (error) {
            console.error("Error decoding token:", error);
          }

          setStatus('success');
          setMessage('Success! Redirecting to dashboard...');
          
          // Redirect after brief delay
          setTimeout(() => {
            navigate('/metrics');
          }, 1000);
        } else {
          setStatus('error');
          // Provide specific error messages based on the response
          let errorMessage = 'Login failed. Please try again.';
          
          if (data.message) {
            if (data.message.includes('already been used')) {
              errorMessage = 'This login link has already been used. Please request a new one.';
            } else if (data.message.includes('expired')) {
              errorMessage = 'This login link has expired. Please request a new one.';
            } else if (data.message.includes('Invalid')) {
              errorMessage = 'This login link is invalid. Please request a new one.';
            } else {
              errorMessage = data.message;
            }
          }
          
          setMessage(errorMessage);
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('Unable to verify login. Please try again.');
      }
  };

  return (
    <div className="verify-login-wrapper">
      <div className="verify-login-background"></div>
      
      <div className="verify-login-container">
        <div className="verify-login-box">
          <div className="verify-logo">
            <h1>DATAHOUSE</h1>
          </div>
          
          <div className={`status-icon ${status}`}>
            {status === 'confirming' && (
              <div className="confirm-icon">
                <svg viewBox="0 0 52 52" fill="#667eea">
                  <path d="M26 0C11.64 0 0 11.64 0 26s11.64 26 26 26 26-11.64 26-26S40.36 0 26 0zm0 48C13.85 48 4 38.15 4 26S13.85 4 26 4s22 9.85 22 22-9.85 22-22 22zm9.5-33.5L23 27l-6.5-6.5L14 23l9 9 15-15-2.5-2.5z"/>
                </svg>
              </div>
            )}
            {status === 'verifying' && (
              <div className="spinner">
                <div className="spinner-circle"></div>
              </div>
            )}
            {status === 'success' && (
              <div className="checkmark">
                <svg viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" />
                  <path fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                </svg>
              </div>
            )}
            {status === 'error' && (
              <div className="error-mark">
                <svg viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" />
                  <path fill="none" d="M16,16 L36,36 M36,16 L16,36" />
                </svg>
              </div>
            )}
          </div>
          
          <h2 className={`verify-message ${status}`}>{message}</h2>
          
          {status === 'confirming' && tokenInfo && (
            <div className="confirm-actions">
              <button onClick={handleConfirmLogin} className="confirm-button">
                Complete Login
              </button>
              <p className="token-info">
                This link expires in {tokenInfo.expiresInMinutes} minutes
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="error-actions">
              <button onClick={() => navigate('/login')} className="return-button">
                Request New Login Link
              </button>
              <p className="error-help-text">
                Having trouble? Make sure to click the most recent email link you received.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyLogin;